import React, { useEffect } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Drawer, FormInstance } from "antd";
import YmsDrawer from "components/drawer";
import DrawerForm from "components/form";
import FormCheckin from "components/form/checkin";
import {
  CheckinOriginEnum,
  IDocumentsData,
  IDocumentType,
  IProcessesData,
  IRegisterCheckinItem,
  ISaleChannelData,
  IVehicleProfileByPlate,
  PlateFormatType,
} from "store/lobby/lobby-model";
import { IUserData } from "store/user-session/user-session-model";
import { IEventsVehicleHistory } from "typings/global";
import { DrawerStatusEnum, TabPaneStatusEnum } from "typings/global.types";
import { Roles } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import EventsHistory from "../../../components/eventsHistory/EventsHistory";
import { ConfirmViewEdit } from "../lobby/components/confirm";
import { OnConfirmCancelEdit } from "./attendance.service";
import ViewRegister from "./components/viewRegister";
import * as S from "./styled/screen.styled";

interface IPropsScreenDrawers {
  checkinRegisterActive: IRegisterCheckinItem;
  disabledButton: boolean;
  excludeButtonDisabled: boolean;
  isConcierge: boolean;
  form: FormInstance;
  formCheckin: FormInstance;
  visibleDrawerCheckinRegisterAttendance: boolean;
  visibleDrawerVehicleEntryAuthorization: boolean;
  inboundProcessesList: Array<IProcessesData>;
  documentsList: IDocumentsData[];
  carrierRef: React.RefObject<HTMLDivElement> | any;
  hasSaleChannel: boolean;
  plateFormatType: PlateFormatType;
  salesChannelList: Array<ISaleChannelData>;
  visibleDrawerViewCheckinRegisterAttendance: boolean;
  visibleDrawerViewMode?: DrawerStatusEnum;
  vehicleEntryAuthorizationRegisterActive?: Array<IEventsVehicleHistory>;
  visibleDrawerViewRegisterAttendance: boolean;
  YES_MESSAGE: string;
  NO_MESSAGE: string;
  formViewOrEditModified?: boolean;
  drawerIsLoading: boolean;
  userData: IUserData;
  tabPaneActive: TabPaneStatusEnum;
  documentType: IDocumentType;
  saleChannelActive?: number;
  processActive: number;
  vehicleProfileByPlate?: IVehicleProfileByPlate | null;
}

interface IDispatchProps {
  visibleFocus: () => void;
  handleOnConfirmDrawerViewRegister: (...args) => void;
  handleOnConfirmDrawerCheckinRegister: (...args) => void;
  attendanceHideDrawerCheckinRegister: (...args) => void;
  lobbyDocumentsFetchAsync: (...args) => void;
  lobbyProcessActive: (...args) => void;
  lobbySaleChannelActive: (...args) => void;
  attendanceHideDrawerViewCheckinRegister: (...args) => void;
  attendanceHideDrawerViewCheckinExclude: (...args) => void;
  attendanceHideDrawerVehicleEntryAuthorization: (...args) => void;
  attendanceHideDrawerViewRegister: (...args) => void;
  attendanceUpdateFormModified: (...args) => void;
  attendanceUpdateTabpaneActive: (...args) => void;
  lobbyCheckinUpdateRegisterActive?: (...args) => void;
  lobbyFetchEntryPermissionAsync?: (...args) => void;
  lobbyUpdateEntryPermissionActive?: (...args) => void;
  lobbyUpdateStepEntryPermissionStatus?: (...args) => void;
  attendanceFetchItemsCheckinAsync?: (...args) => void;
  lobbyFetchVehicleProfileByPlateAsync?: (...args) => void;
  lobbyUpdateVehicleProfileByPlate?: (...args) => void;
}

type IDrawers = IPropsScreenDrawers & IDispatchProps;

const Drawers = ({
  userData,
  checkinRegisterActive,
  visibleDrawerViewMode,
  YES_MESSAGE,
  NO_MESSAGE,
  formViewOrEditModified,
  disabledButton,
  excludeButtonDisabled,
  form,
  formCheckin,
  visibleDrawerCheckinRegisterAttendance,
  inboundProcessesList,
  documentsList,
  carrierRef,
  documentType,
  hasSaleChannel,
  salesChannelList,
  visibleDrawerViewCheckinRegisterAttendance,
  visibleDrawerVehicleEntryAuthorization,
  vehicleEntryAuthorizationRegisterActive,
  visibleDrawerViewRegisterAttendance,
  tabPaneActive,
  drawerIsLoading,
  saleChannelActive,
  plateFormatType,
  processActive,
  vehicleProfileByPlate,
  lobbyCheckinUpdateRegisterActive,
  attendanceHideDrawerViewRegister,
  handleOnConfirmDrawerViewRegister,
  handleOnConfirmDrawerCheckinRegister,
  visibleFocus,
  lobbyDocumentsFetchAsync,
  lobbyProcessActive,
  lobbySaleChannelActive,
  attendanceHideDrawerViewCheckinRegister,
  attendanceHideDrawerCheckinRegister,
  attendanceHideDrawerViewCheckinExclude,
  attendanceHideDrawerVehicleEntryAuthorization,
  attendanceUpdateFormModified,
  attendanceUpdateTabpaneActive,
  lobbyFetchEntryPermissionAsync,
  lobbyUpdateEntryPermissionActive,
  lobbyUpdateStepEntryPermissionStatus,
  attendanceFetchItemsCheckinAsync,
  lobbyFetchVehicleProfileByPlateAsync,
  lobbyUpdateVehicleProfileByPlate,
}: IDrawers): JSX.Element => {
  const optionsViewOrEditDrawer = {
    VIEW: { title: useTranslation({ id: "view_check_in" }), confirmbuttontxt: useTranslation({ id: "edit" }), customrenderconfirmbutton: null },
    EDIT: {
      title: useTranslation({ id: "edit_check_in" }),
      confirmbuttontxt: useTranslation({ id: "save" }),
      customrenderconfirmbutton: <ConfirmViewEdit handleOnConfirm={() => handleOnConfirmDrawerViewRegister()} okText={YES_MESSAGE} cancelText={NO_MESSAGE} />,
    },
  };

  const permissionRolesExcludeCheckin = [Roles.ADMIN, Roles.ATTENDANCE, Roles.CONCIERGE_ATTENDANCE];
  const checkRolesPermissionToViewButtons = (Object.values(permissionRolesExcludeCheckin) as string[]).includes(userData.role);
  const optionsExcludeCheckin = (): boolean => {
    if (checkinRegisterActive?.checkinOrigin === CheckinOriginEnum.Manually && visibleDrawerViewMode === DrawerStatusEnum.View && checkRolesPermissionToViewButtons) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (!visibleDrawerViewCheckinRegisterAttendance) {
      lobbyCheckinUpdateRegisterActive({ checkinRegisterActive: { processId: null } });
    }
  }, [visibleDrawerViewCheckinRegisterAttendance]);

  return (
    <>
      <YmsDrawer
        title={useTranslation({ id: "register_new_vehicle" })}
        placement="right"
        closable={false}
        disabled={disabledButton}
        open={visibleDrawerCheckinRegisterAttendance}
        afterOpenChange={visibleFocus}
        width="900"
        cancelbuttontxt={useTranslation({ id: "cancel" })}
        confirmbuttontxt={useTranslation({ id: "register" })}
        maskClosable={true}
        onConfirm={handleOnConfirmDrawerCheckinRegister}
        onClose={attendanceHideDrawerCheckinRegister}
        onExclude={attendanceHideDrawerViewCheckinExclude}
        destroyOnClose={true}
        tabPaneActive={tabPaneActive}
      >
        <DrawerForm
          form={formCheckin}
          inboundProcessesList={inboundProcessesList}
          lobbyDocumentsFetchAsync={lobbyDocumentsFetchAsync}
          documentsList={documentsList}
          carrierRef={carrierRef}
          lobbyProcessActive={lobbyProcessActive}
          hasSaleChannel={hasSaleChannel}
          lobbySaleChannelActive={lobbySaleChannelActive}
          salesChannelList={salesChannelList}
          visibleDrawerView={visibleDrawerViewRegisterAttendance}
          visibleDrawerAddNewRegister={visibleDrawerCheckinRegisterAttendance}
          visibleDrawerViewMode={visibleDrawerViewMode}
          drawerIsLoading={drawerIsLoading}
          documentType={documentType}
          hasEntryPermission={false}
          plateFormatType={plateFormatType}
          vehicleProfileByPlate={vehicleProfileByPlate}
          useVehicleProfileEndpoint={true}
          lobbyFetchVehicleProfileByPlateAsync={lobbyFetchVehicleProfileByPlateAsync}
          lobbyUpdateVehicleProfileByPlate={lobbyUpdateVehicleProfileByPlate}
          lobbyFetchEntryPermissionAsync={lobbyFetchEntryPermissionAsync}
          lobbyUpdateEntryPermissionActive={lobbyUpdateEntryPermissionActive}
          lobbyUpdateStepEntryPermissionStatus={lobbyUpdateStepEntryPermissionStatus}
          saleChannelActive={saleChannelActive}
        />
      </YmsDrawer>
      <YmsDrawer
        title={optionsViewOrEditDrawer[visibleDrawerViewMode].title}
        placement="right"
        closable={false}
        showConfirmButton={checkRolesPermissionToViewButtons}
        open={visibleDrawerViewCheckinRegisterAttendance}
        width="900"
        cancelbuttontxt={useTranslation({ id: "cancel" })}
        confirmbuttontxt={optionsViewOrEditDrawer[visibleDrawerViewMode].confirmbuttontxt}
        customrenderconfirmbutton={optionsViewOrEditDrawer[visibleDrawerViewMode].customrenderconfirmbutton}
        maskClosable={true}
        onClose={attendanceHideDrawerViewCheckinRegister}
        onExclude={attendanceHideDrawerViewCheckinExclude}
        destroyOnClose={true}
        excludeButtonDisabled={excludeButtonDisabled}
        showExcludeButton={optionsExcludeCheckin()}
        excludeButtontxt={useTranslation({ id: "exclude" })}
        onConfirm={handleOnConfirmDrawerViewRegister}
        tabPaneActive={tabPaneActive}
        disabled={drawerIsLoading}
        customrendercancelbutton={
          <OnConfirmCancelEdit
            handleOnConfirm={() => attendanceHideDrawerCheckinRegister()}
            okText={YES_MESSAGE}
            cancelText={NO_MESSAGE}
            visibleDrawerViewMode={visibleDrawerViewMode}
            formViewOrEditModified={formViewOrEditModified}
            attendanceHideDrawerViewRegister={attendanceHideDrawerViewCheckinRegister}
            disabled={drawerIsLoading}
          />
        }
      >
        <FormCheckin
          form={form}
          documentsList={documentsList}
          visibleDrawerViewModelAttendance={visibleDrawerViewMode}
          lobbyDocumentsFetchAsync={lobbyDocumentsFetchAsync}
          lobbyProcessActive={lobbyProcessActive}
          inboundProcessesList={inboundProcessesList}
          hasSaleChannel={hasSaleChannel}
          lobbySaleChannelActive={lobbySaleChannelActive}
          salesChannelList={salesChannelList}
          attendanceUpdateTabpaneActive={attendanceUpdateTabpaneActive}
          hasItems={true}
          drawerIsLoading={drawerIsLoading}
          documentType={documentType}
          hasEntryPermission={false}
          plateFormatType={plateFormatType}
          lobbyFetchEntryPermissionAsync={lobbyFetchEntryPermissionAsync}
          lobbyUpdateEntryPermissionActive={lobbyUpdateEntryPermissionActive}
          lobbyUpdateStepEntryPermissionStatus={lobbyUpdateStepEntryPermissionStatus}
          saleChannelActive={saleChannelActive}
          attendanceFetchItemsCheckinAsync={attendanceFetchItemsCheckinAsync}
          processActive={processActive}
        />
      </YmsDrawer>
      <YmsDrawer
        title={optionsViewOrEditDrawer[visibleDrawerViewMode].title}
        placement="right"
        closable={false}
        open={visibleDrawerViewRegisterAttendance}
        width="900"
        showConfirmButton={checkRolesPermissionToViewButtons}
        cancelbuttontxt={useTranslation({ id: "cancel" })}
        confirmbuttontxt={optionsViewOrEditDrawer[visibleDrawerViewMode].confirmbuttontxt}
        customrenderconfirmbutton={optionsViewOrEditDrawer[visibleDrawerViewMode].customrenderconfirmbutton}
        maskClosable={true}
        destroyOnClose={true}
        onConfirm={handleOnConfirmDrawerViewRegister}
        onClose={attendanceHideDrawerViewRegister}
        tabPaneActive={tabPaneActive}
        disabled={drawerIsLoading}
        customrendercancelbutton={
          <OnConfirmCancelEdit
            handleOnConfirm={() => attendanceHideDrawerViewRegister()}
            okText={YES_MESSAGE}
            cancelText={NO_MESSAGE}
            visibleDrawerViewMode={visibleDrawerViewMode}
            formViewOrEditModified={formViewOrEditModified}
            attendanceHideDrawerViewRegister={attendanceHideDrawerViewRegister}
            disabled={drawerIsLoading}
          />
        }
      >
        <ViewRegister
          form={form}
          documentsList={documentsList}
          hasSaleChannel={hasSaleChannel}
          salesChannelList={salesChannelList}
          inboundProcessesList={inboundProcessesList}
          visibleDrawerViewMode={visibleDrawerViewMode}
          visibleDrawerViewRegisterAttendance={visibleDrawerViewRegisterAttendance}
          lobbyProcessActive={lobbyProcessActive}
          lobbySaleChannelActive={lobbySaleChannelActive}
          lobbyDocumentsFetchAsync={lobbyDocumentsFetchAsync}
          attendanceUpdateFormModified={attendanceUpdateFormModified}
          documentType={documentType}
          plateFormatType={plateFormatType}
        />
      </YmsDrawer>
      <Drawer
        title={useTranslation({ id: "events_history" })}
        placement="right"
        open={visibleDrawerVehicleEntryAuthorization}
        width="400"
        maskClosable={true}
        closable={false}
        onClose={attendanceHideDrawerVehicleEntryAuthorization}
        destroyOnClose={true}
        extra={
          <S.CloseDrawer onClick={attendanceHideDrawerVehicleEntryAuthorization}>
            <CloseOutlined className="closeIcon" />
          </S.CloseDrawer>
        }
      >
        <EventsHistory data={vehicleEntryAuthorizationRegisterActive} drawerIsLoading={drawerIsLoading} />
      </Drawer>
    </>
  );
};

export default Drawers;
